import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Card, CardHeader, CardBody, Form } from 'reactstrap';
import ModalLoader from '../../components/ModalLoader';
import {
  startSetUserSettings,
  startEditUserSettings
} from '../../actions/profile';
import { deserializeData, serializeData } from './userSettingsSerializer';
import FloatingModal from '../../components/Modal/FloatingModal';
import UserModalOriginationSection from './UserModalOriginationSection';
import UserModalDestinationSection from './UserModalDestinationSection';

const UserModal = ({
  userSettings,
  activeLanguage,
  toggleModal,
  startSetUserSettings,
  startEditUserSettings,
  translate,
  isModalOpen
}) => {
  const [userState, setUserState] = useState({
    activeTab: 'ORIGINATION',
    id: null,
    description: '',
    domain: '',
    allowed_origination_countries: [],
    allowed_destination_countries: [],
    isLoading: true,
    message: ''
  });

  useEffect(() => {
    startSetUserSettings().then(settingsResponse => {
      const settings = deserializeData(
        _.cloneDeep(settingsResponse || userSettings)
      );
      setUserState(prev => ({ ...prev, ...settings, isLoading: false }));
    });
    // eslint-disable-next-line
  }, []);

  const onNonGeoChange = () => {
    const { allowed_destination_countries } = userState;
    const updatedCountries = [...allowed_destination_countries];

    if (updatedCountries.includes('001')) {
      const index = updatedCountries.indexOf('001');
      updatedCountries.splice(index, 1);
    } else {
      updatedCountries.push('001');
    }

    setUserState(prev => ({
      ...prev,
      allowed_destination_countries: updatedCountries
    }));
  };

  const onOriginationChange = selected => {
    setUserState(prev => ({
      ...prev,
      allowed_origination_countries: selected
    }));
  };

  const onDestinationChange = selected => {
    setUserState(prev => ({
      ...prev,
      allowed_destination_countries: selected
    }));
  };

  const handleCancel = () => {
    toggleModal();
  };

  const onSubmit = async () => {
    const { activeTab, isLoading, message, ...rest } = userState;

    const updates = serializeData(rest);
    const origination = updates.allowed_origination_countries;
    const destination = updates.allowed_destination_countries;

    if (origination.length === 0) {
      setUserState(prev => ({
        ...prev,
        message: translate('user_management_error_origin_country')
      }));
      return;
    }

    if (destination.length === 0) {
      setUserState(prev => ({
        ...prev,
        message: translate('user_management_error_destination_country')
      }));
      return;
    }

    setUserState(prev => ({
      ...prev,
      message: translate('general_saving')
    }));
    try {
      await startEditUserSettings(updates);
      toggleModal();
    } catch (e) {
      setUserState(prev => ({
        ...prev,
        message: translate('apiError_request')
      }));
    }
  };

  const {
    allowed_origination_countries,
    allowed_destination_countries,
    description,
    isLoading,
    message
  } = userState;

  if (isLoading) {
    return <ModalLoader />;
  }

  const modalTitle = description
    ? `${description} - ${translate('user_management_user_settings')}`
    : translate('user_management_user_settings');

  return (
    <FloatingModal
      isOpen={isModalOpen}
      toggleModal={toggleModal}
      header={modalTitle}
      translate={translate}
      onCancel={handleCancel}
      onConfirm={onSubmit}
      size="large"
    >
      <section
        title={translate('user_management_origin_countries')}
        id="origination"
      >
        <Form>
          <Card>
            {message && (
              <CardHeader
                className={
                  message === translate('general_saving')
                    ? 'text-info'
                    : 'text-danger'
                }
              >
                <i
                  className={
                    message === translate('general_saving')
                      ? 'far fa-info-circle'
                      : 'far fa-exclamation-triangle'
                  }
                />{' '}
                {message}
              </CardHeader>
            )}
            <CardBody>
              <UserModalOriginationSection
                allowed_origination_countries={allowed_origination_countries}
                onChange={onOriginationChange}
                activeLanguage={activeLanguage}
                translate={translate}
              />
            </CardBody>
          </Card>
        </Form>
      </section>

      <section
        title={translate('user_management_destination_country')}
        id="destination"
      >
        <UserModalDestinationSection
          allowed_destination_countries={allowed_destination_countries}
          onChange={onDestinationChange}
          onNonGeoChange={onNonGeoChange}
          activeLanguage={activeLanguage}
          translate={translate}
        />
      </section>
    </FloatingModal>
  );
};

const mapStateToProps = state => {
  return {
    userSettings: state.profile.userSettings,
    activeLanguage: state.settings.lang
  };
};

const mapDispatchToProps = dispatch => ({
  startSetUserSettings: () => dispatch(startSetUserSettings()),
  startEditUserSettings: updates => dispatch(startEditUserSettings(updates))
});

export default connect(mapStateToProps, mapDispatchToProps)(UserModal);
